import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String }
  
  connect() {
    this.draggedElement = null
    this.abortController = new AbortController()
    this.setupDragAndDrop()
  }

  disconnect() {
    this.abortController.abort()
  }
  
  setupDragAndDrop() {
    const items = this.element.querySelectorAll('[data-sortable-item]')
    
    items.forEach(item => {
      const handle = item.querySelector('[data-sortable-handle]')
      if (!handle) return
      
      // Make the whole item draggable, not just the handle
      item.draggable = true
      handle.style.cursor = 'grab'
      handle.style.userSelect = 'none'
      handle.style.webkitUserSelect = 'none'
      
      // Make SVG non-interactive
      const svg = handle.querySelector('svg')
      if (svg) {
        svg.style.pointerEvents = 'none'
        svg.querySelectorAll('*').forEach(el => {
          el.style.pointerEvents = 'none'
        })
      }
      
      // Only allow dragging when mousedown on handle
      let isDraggingAllowed = false
      
      handle.addEventListener('mousedown', (e) => {
        isDraggingAllowed = true
      }, { signal: this.abortController.signal })

      document.addEventListener('mouseup', () => {
        isDraggingAllowed = false
      }, { signal: this.abortController.signal })
      
      // Drag start
      item.addEventListener('dragstart', (e) => {
        if (!isDraggingAllowed) {
          e.preventDefault()
          return
        }

        this.draggedElement = item
        item.classList.add('opacity-50')
        handle.style.cursor = 'grabbing'

        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', item.dataset.scoringRuleId)
      }, { signal: this.abortController.signal })

      // Drag end
      item.addEventListener('dragend', (e) => {
        item.classList.remove('opacity-50')
        handle.style.cursor = 'grab'
        isDraggingAllowed = false

        // Clear any drop indicators
        this.element.querySelectorAll('[data-sortable-item]').forEach(el => {
          el.classList.remove('border-t-2', 'border-blue-500')
        })

        this.updateOrder()
      }, { signal: this.abortController.signal })

      // Drop zone events on items
      item.addEventListener('dragover', (e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'

        if (this.draggedElement && this.draggedElement !== item) {
          const rect = item.getBoundingClientRect()
          const midY = rect.top + rect.height / 2

          // Perform the visual reordering during dragover
          if (e.clientY < midY) {
            // Insert before this item
            item.parentNode.insertBefore(this.draggedElement, item)
          } else {
            // Insert after this item
            item.parentNode.insertBefore(this.draggedElement, item.nextSibling)
          }
        }
      }, { signal: this.abortController.signal })

      item.addEventListener('drop', (e) => {
        e.preventDefault()
        // The reordering already happened in dragover, so we just need to prevent default
      }, { signal: this.abortController.signal })

      item.addEventListener('dragleave', (e) => {
        item.classList.remove('border-t-2', 'border-blue-500')
      }, { signal: this.abortController.signal })
    })
  }
  
  async updateOrder() {
    const ruleIds = Array.from(this.element.querySelectorAll('[data-scoring-rule-id]'))
      .map(el => el.dataset.scoringRuleId)

    try {
      const response = await fetch(`${this.urlValue}/reorder`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          rule_ids: ruleIds
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update order')
      }

      // Visual confirmation
      if (this.draggedElement) {
        this.draggedElement.classList.add('bg-green-50', 'transition-colors', 'duration-300')
        setTimeout(() => {
          this.draggedElement.classList.remove('bg-green-50')
        }, 1000)
      }
      
      // Update priority badges
      this.updatePriorityBadges()
    } catch (error) {
      console.error('Error updating order:', error)
      window.location.reload()
    }
  }
  
  updatePriorityBadges() {
    const items = this.element.querySelectorAll('[data-sortable-item]')
    items.forEach((item, index) => {
      // Find the priority badge - it's in a span with "Priority" text
      const priorityBadge = Array.from(item.querySelectorAll('span'))
        .find(span => span.textContent.includes('Priority'))
      
      if (priorityBadge) {
        // Update the priority number (0-based index)
        priorityBadge.textContent = `Priority ${index}`
      }
    })
  }
}